import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { getUser } from "@/lib/auth";
import { api } from "@/lib/api";
import { LayoutDashboard, Receipt, ClipboardList, AlertOctagon, Map, Users, Store as StoreIcon, MapPinned, Package, Wallet, ShieldCheck, Warehouse, MessageCircle, NotebookPen, Clock, FolderKanban, Briefcase, ClipboardEdit, UserCheck, BadgeIndianRupee, MessagesSquare, DatabaseBackup } from "lucide-react";

const items = {
  SuperAdmin: [
    { to: "/portfolio", label: "Portfolio", icon: Briefcase, end: true },
    { to: "/admin/projects", label: "Projects", icon: FolderKanban },
    { to: "/admin", label: "Project Dashboard", icon: LayoutDashboard },
    { to: "/admin/stores", label: "Manage Stores", icon: StoreIcon },
    { to: "/admin/users", label: "Manage Users", icon: Users },
    { to: "/admin/approvals", label: "Sign-up Approvals", icon: UserCheck },
    { to: "/admin/salary", label: "Promoter Salary", icon: BadgeIndianRupee },
    { to: "/admin/audit", label: "Audit Log", icon: ShieldCheck },
    { to: "/admin/backup", label: "Data Backup", icon: DatabaseBackup },
  ],
  Admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/stores", label: "Manage Stores", icon: StoreIcon },
    { to: "/admin/zones", label: "Zones", icon: MapPinned },
    { to: "/admin/stock", label: "Stock & Dispatch", icon: Warehouse },
    { to: "/admin/restock", label: "Restock Needed", icon: AlertOctagon },
    { to: "/admin/procurement", label: "Procurement", icon: Package },
    { to: "/admin/expenses", label: "Expenses", icon: Wallet },
    { to: "/admin/billing", label: "Billing", icon: Receipt },
    { to: "/admin/issues", label: "Issues", icon: AlertOctagon },
    { to: "/admin/daily-entries", label: "Daily Entries", icon: ClipboardEdit },
    { to: "/admin/late-entries", label: "Late Entries", icon: Clock },
    { to: "/admin/users", label: "Manage Users", icon: Users },
    { to: "/admin/approvals", label: "Sign-up Approvals", icon: UserCheck },
    { to: "/admin/salary", label: "Promoter Salary", icon: BadgeIndianRupee },
    { to: "/admin/client-notes", label: "Notes to Coca-Cola", icon: MessageCircle },
    { to: "/admin/notes", label: "Private Notes", icon: NotebookPen },
    { to: "/admin/audit", label: "Audit Log", icon: ShieldCheck },
    { to: "/admin/backup", label: "Data Backup", icon: DatabaseBackup },
  ],
  Supervisor: [
    // ITEM 17 re-audit (2026-02-29) — every entry now resolves under /coordinator/* so the URL bar stays consistent
    { to: "/coordinator", label: "My Stores", icon: Map, end: true },
    { to: "/coordinator/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/coordinator/stock", label: "Stock & Dispatch", icon: Warehouse },
    { to: "/coordinator/restock", label: "Restock Needed", icon: AlertOctagon },
    { to: "/coordinator/procurement", label: "Procurement", icon: Package },
    { to: "/coordinator/expenses", label: "Expenses", icon: Wallet },
    { to: "/coordinator/issues", label: "Issues", icon: AlertOctagon },
    { to: "/coordinator/daily-entries", label: "Daily Entries", icon: ClipboardEdit },
    { to: "/coordinator/late-entries", label: "Late Entries", icon: Clock },
  ],
  "Coca-Cola": [
    { to: "/coca-cola", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/coca-cola/reports", label: "Reports", icon: ClipboardList },
  ],
  Client: [
    { to: "/coca-cola", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/coca-cola/reports", label: "Reports", icon: ClipboardList },
  ],
};
// IT keeps the same underlying nav as Supervisor (they share role scope). Project Lead → Manager URL variant.
items.IT = items.Supervisor;
items["Project Lead"] = [
  // ITEM 17 re-audit — Manager URLs mirror /coordinator/* under /manager/*
  { to: "/manager", label: "My Stores", icon: Map, end: true },
  { to: "/manager/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/manager/stock", label: "Stock & Dispatch", icon: Warehouse },
  { to: "/manager/restock", label: "Restock Needed", icon: AlertOctagon },
  { to: "/manager/procurement", label: "Procurement", icon: Package },
  { to: "/manager/expenses", label: "Expenses", icon: Wallet },
  { to: "/manager/issues", label: "Issues", icon: AlertOctagon },
  { to: "/manager/daily-entries", label: "Daily Entries", icon: ClipboardEdit },
  { to: "/manager/late-entries", label: "Late Entries", icon: Clock },
  { to: "/manager/approvals", label: "Sign-up Approvals", icon: UserCheck },
];

// Team Chat — one shared room, added for EVERY role. The badge count is populated by
// the parent NavItem via /api/chat/unread-count.
Object.keys(items).forEach((role) => {
  items[role] = [...items[role], { to: "/chat", label: "Team Chat", icon: MessagesSquare, badgeKind: "chat" }];
});

export const SideNav = () => {
  const u = getUser();
  const links = items[u?.role] || [];
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    if (!u) return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/chat/unread-count");
        if (!stopped) setChatUnread(data.unread || 0);
      } catch { /* ignore transient errors */ }
    };
    tick();
    const t = setInterval(tick, 20000);
    // Refresh when the tab regains focus so a returning user sees a fresh count.
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { stopped = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [u]);

  return (
    <nav className="hidden md:flex flex-col gap-1 w-56 shrink-0 border-r border-gray-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 px-3 py-2">Menu</div>
      {links.map(({ to, label, icon: Icon, end, badgeKind }) => (
        <NavLink
          key={to} to={to} end={end}
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-red-50 text-brand-red" : "text-gray-700 hover:bg-gray-50"
            }`
          }
        >
          <Icon className="h-4 w-4" /> <span className="flex-1">{label}</span>
          {badgeKind === "chat" && chatUnread > 0 && (
            <span data-testid="chat-unread-badge"
              className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand-red text-white text-[10px] font-bold flex items-center justify-center">
              {chatUnread > 99 ? "99+" : chatUnread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
};
